import { auditTrendDetail } from "@/api/trend-audit";
import { Case, When } from "@/components/When";
import { API_RESULT_CODE, DictType } from "@/config/types/common";
import { TrendItem, TrendStatus } from "@/config/types/trend";
import { Button, Dialog, SafeArea, Toast } from "antd-mobile";
import { useEffect, useState } from "react";
import AliasTextareaPopup from "../../message/AliasTextareaPopup";
import "./BtnGroup.less";
import { debounce } from "lodash";

interface BtnGroupProps {
  data: TrendItem;
  onSuccess: (newAuditStatus: TrendStatus) => void;
}

const BtnGroup = ({ data, onSuccess }: BtnGroupProps) => {
  const [showFailedPop, setShowFailedPop] = useState(false);
  const [loading, setLoading] = useState(false);

  const { trendsId, auditStatus } = data;

  /**
   * 调整状态成功后，存储该动态的 trendId 和 auditStatus，
   * 用于回到列表时，能同步调整该动态的状态；
   */
  const cacheKey = "trend-audit-detail";

  /**
   * 解决移动端h5中的textarea无法滚动的问题
   * @see https://blog.csdn.net/qq_41219789/article/details/106218145
   */
  useEffect(() => {
    const listener = (event: any) => {
      if (event.target.tagName.toUpperCase() === "TEXTAREA") {
        event.stopPropagation();
      }
    };
    if (showFailedPop) {
      window.addEventListener("touchmove", listener, true);
      return () => window.removeEventListener("touchmove", listener);
    } else {
      window.removeEventListener("touchmove", listener);
    }
  }, [showFailedPop]);

  // 审核
  const audit = async (values: any) => {
    setLoading(true);
    values.trends = [{ trendsId }];

    try {
      const { code, msg } = await auditTrendDetail(values);
      if (code === API_RESULT_CODE.SUCCESS) {
        Toast.show("审核成功");
        window.sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ trendsId, auditStatus: values.auditStatus })
        );
        onSuccess(values.auditStatus);
      } else {
        Toast.show(msg || "审核失败");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setShowFailedPop(false);
    }
  };

  // 使用 debounce 包装 audit
  const debouncedAudit = debounce(audit, 300); // 300 毫秒的防抖时间

  // 已选择不通过的原因，设置为不通过
  const handleFailed = async (values: any) => {
    try {
      values.auditStatus = TrendStatus.FAIL;

      debouncedAudit(values);
    } catch (err) {
      console.error(err);
    }
  };

  // 设置为通过
  const handlePass = () => {
    Dialog.confirm({
      content: "确定将该动态调整为审核通过吗？",
      onConfirm() {
        return debouncedAudit({ auditStatus: TrendStatus.SUCCESSS, auditReason: "通过" });
      },
    });
  };

  // 显示不通过的弹窗
  const handleShowFailPop = () => {
    setShowFailedPop(true);
  };

  return (
    <div className="detail-btn-group">
      <div className="btn-group-content">
        <When>
          <Case if={auditStatus === TrendStatus.SUCCESSS}>
            <Button className="failed-btn" style={{ width: "100%" }} onClick={handleShowFailPop}>
              调整为未通过状态
            </Button>
          </Case>
          <Case elseif={auditStatus === TrendStatus.FAIL}>
            <Button className="failed-btn" style={{ width: "100%" }} onClick={handlePass}>
              调整为通过状态
            </Button>
          </Case>
          <Case elseif={data.lockedBy !== "" && data.lockedBy !== "0" && data.meLocked !== "1"}>
            <Button
              className="failed-btn"
              style={{ width: "100%" }}
              onClick={() => {
                Toast.show("该动态正在审核中");
              }}
            >
              {data.lockedUserName}审核中
            </Button>
          </Case>
          <Case else>
            <Button className="failed-btn" onClick={handleShowFailPop}>
              不通过
            </Button>
            <Button color="primary" className="suc-btn" onClick={handlePass}>
              通过
            </Button>
          </Case>
        </When>
      </div>
      <SafeArea position="bottom" />
      <AliasTextareaPopup
        visible={showFailedPop}
        alias={DictType.MOMENT_AUDIT_DENY}
        name="auditReason"
        title="审核未通过"
        loading={loading}
        onClose={() => setShowFailedPop(false)}
        onOk={handleFailed}
      />
    </div>
  );
};
export default BtnGroup;
